<template>
  <table v-if="!paused" aria-label="2048 game board">
    <caption class="visually-hidden">2048 game board, use WASD or arrow keys to move tiles</caption>
    <tbody>
      <tr v-for="(tiles, row) in partition(state, props.tableSize)">
        <td v-for="(tile, col) in tiles">
          <div class="d-flex align-items-center justify-content-center target" :style="{'background-color': numberToColor[tile], 'color': textColor[tile]}" role="img" :aria-label="`Row ${row + 1} column ${col + 1}: ${tile === 0 ? 'empty' : tile}`">
            {{tile === 0 ? '' : tile}}
          </div>
        </td>
      </tr>
    </tbody>
  </table>
  <div v-else class="board-placeholder d-flex align-items-center justify-content-center text-muted" role="status">
    Board rendering paused for training speed
  </div>
</template>

<script lang="ts" setup>
import {onUnmounted, onMounted, ref, Ref, watch} from "vue";
import {Direction, GameEnv} from "../GameEnv";

const emit = defineEmits(["lost", "won", "update:current_score"]);

const numberToColor: Record<number, string> = {
  0: "#ff9b00",
  2: "#4d4d4d",
  4: "#7a6a00",
  8: "#ffffff",
  16: "#ffb59b",
  32: "#ffd6cc",
  64: "#ffcccc",
  128: "#666600",
  256: "#666600",
  512: "#0c6600",
  1024: "#005c4f",
  2048: "#ccd9ff"
}

const textColor: Record<number, string> = {
  0: "#ff9b00",
  2: "#ffffff",
  4: "#ffffff",
  8: "#bd5000",
  16: "#5a1f00",
  32: "#8c2800",
  64: "#8c0000",
  128: "#ffffff",
  256: "#ffffff",
  512: "#ffffff",
  1024: "#ffffff",
  2048: "#00117a"
}

const props = withDefaults(defineProps<{ current_score: number, tableSize: number, paused?: boolean, blockInput?: boolean }>(), { paused: false, blockInput: false });

let env = new GameEnv(props.tableSize);

const state: Ref<number[]> = ref([...env.state]);

const lost = ref(false);
const won = ref(false);
const current_for_orchestrator = ref(props.current_score);

function partition(items: number[], size: number): number[][] {
  const p = [];
  for (let i = Math.floor(items.length/size); i-->0; ) {
    p[i] = items.slice(i*size, (i+1)*size);
  }
  return p;
}

function syncFromEnv() {
  if (env.won && !won.value) {
    emit("won");
  }
  if (env.lost && !lost.value) {
    emit("lost");
  }
  won.value = env.won;
  lost.value = env.lost;
  state.value = [...env.state];
  current_for_orchestrator.value = env.score;
}

function legalMoves(): boolean[] {
  return env.legalMoves();
}

function shift(direction: Direction): boolean {
  const result = env.step(direction);
  if (result.moved) {
    syncFromEnv();
    if (result.gained > 0) {
      emit("update:current_score", current_for_orchestrator.value);
    }
  }
  return result.moved;
}

function keypress_listener(event: KeyboardEvent) {
  if (props.paused || props.blockInput) {
    return;
  }
  if (lost.value || won.value) {
    return;
  }
  if (event.target instanceof HTMLElement && (event.target.tagName === "INPUT" || event.target.tagName === "SELECT" || event.target.tagName === "TEXTAREA" || event.target.isContentEditable)) {
    return;
  }
  switch (event.key.toLowerCase()) {
    case "w":
    case "arrowup":
      shift("up")
      break
    case "a":
      shift("left")
      break
    case "s":
      shift("down")
      break
    case "d":
      shift("right")
      break
    case "arrowleft":
      shift("left")
      event.preventDefault();
      break
    case "arrowdown":
      shift("down")
      event.preventDefault();
      break
    case "arrowright":
      shift("right")
      event.preventDefault();
      break
  }
}

onUnmounted(() => {
  document.removeEventListener("keydown", keypress_listener);
});

onMounted(() => {
  document.addEventListener("keydown", keypress_listener);
});

watch(() => props.tableSize, () => {
  env = new GameEnv(props.tableSize);
  reset();
});

function reset() {
  env.reset();
  syncFromEnv();
  emit("update:current_score", current_for_orchestrator.value);
}

function displayState(external: number[], score: number, hasWon: boolean, hasLost: boolean) {
  env.setState(external, score, hasWon, hasLost);
  syncFromEnv();
  emit("update:current_score", current_for_orchestrator.value);
}

defineExpose({
  reset,
  displayState,
  state,
  current_score: current_for_orchestrator,
  won,
  lost,
  shift,
  legalMoves
});
</script>

<style scoped lang="sass">

$small-size: 3rem
$big-size: 5rem
@media (max-width: 767.98px)
  .target
    min-height: $small-size
    height: $small-size
    min-width: $small-size
    width: $small-size

@media (min-width: 768px)
  .target
    min-height: $big-size
    height: $big-size
    min-width: $big-size
    width: $big-size

.board-placeholder
  min-height: 5rem

</style>